import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShoppingCart, Check } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ErrorState from '../../components/common/ErrorState';
import { Skeleton } from '../../components/common/Skeleton';
import { fetchWreathById } from '../../utils/api';
import { formatCurrency } from '../../utils/formatters';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';

const FALLBACK_IMAGE = '/images/wreaths/placeholder.svg';
const MAX_QUANTITY = 99;

// 추천 근조 리본 문구 템플릿
const RIBBON_TEMPLATES = [
  '謹弔 (근조)',
  '삼가 고인의 명복을 빕니다',
  '故人의 冥福을 빕니다',
  '弔意 (조의)',
  '삼가 조의를 표합니다',
];

const WreathDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const { showSuccess } = useToast();

  const [wreath, setWreath] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [imgError, setImgError] = useState(false);

  // 근조 리본 문구
  const [ribbon, setRibbon] = useState({ left: '', right: '', sender: '' });

  const loadWreath = useCallback(async () => {
    setLoading(true);
    setError(false);
    setNotFound(false);
    try {
      const data = await fetchWreathById(id);
      if (data) {
        setWreath(data);
      } else {
        setNotFound(true);
      }
    } catch (e) {
      console.error('화환 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadWreath();
  }, [loadWreath]);

  const handleDecrease = () => {
    setQuantity((q) => (q > 1 ? q - 1 : 1));
  };

  const handleIncrease = () => {
    setQuantity((q) => (q < MAX_QUANTITY ? q + 1 : MAX_QUANTITY));
  };

  const handleQuantityInput = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') {
      setQuantity(1);
      return;
    }
    let next = parseInt(raw, 10);
    if (Number.isNaN(next) || next < 1) next = 1;
    if (next > MAX_QUANTITY) next = MAX_QUANTITY;
    setQuantity(next);
  };

  const buildOptions = () => {
    const hasRibbon = ribbon.left || ribbon.right || ribbon.sender;
    return hasRibbon ? { ribbon: { ...ribbon } } : {};
  };

  const handleAddToCart = () => {
    if (!wreath?.inStock) return;
    addToCart(wreath, quantity, buildOptions());
    showSuccess(`${wreath.name}을(를) 장바구니에 담았습니다`);
  };

  const handleBuyNow = () => {
    if (!wreath?.inStock) return;
    addToCart(wreath, quantity, buildOptions());
    navigate('/cart');
  };

  const applyTemplate = (side, text) => {
    setRibbon((prev) => ({ ...prev, [side]: text }));
  };

  const getCategoryBadgeColor = (category) => {
    switch (category) {
      case '프리미엄':
        return 'bg-primary text-white';
      case '스탠다드':
        return 'bg-accent-400 text-primary';
      case '이코노미':
        return 'bg-accent-200 text-accent-800';
      default:
        return 'bg-accent-200 text-accent-800';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-6 md:py-8">
          <Skeleton className="h-6 w-32 mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            <Skeleton className="aspect-square w-full" />
            <div className="space-y-4">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-10 w-1/2" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <ErrorState
            title="화환 정보를 불러오지 못했습니다"
            message="잠시 후 다시 시도해주세요."
            onRetry={loadWreath}
          />
        </div>
      </MainLayout>
    );
  }

  if (notFound || !wreath) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <ErrorState
            title="화환을 찾을 수 없습니다"
            message="요청하신 상품이 존재하지 않거나 삭제되었습니다."
            onRetry={() => navigate('/wreaths')}
            retryLabel="화환 목록으로"
          />
        </div>
      </MainLayout>
    );
  }

  const alreadyInCart = isInCart(wreath.id);

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* 뒤로 가기 버튼 */}
        <button
          onClick={() => navigate('/wreaths')}
          className="flex items-center gap-2 text-accent-600 hover:text-primary mb-4 md:mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>화환 목록으로</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* 이미지 영역 */}
          <div className="relative aspect-square rounded-lg overflow-hidden bg-accent-100 border border-accent-200">
            <img
              src={imgError ? FALLBACK_IMAGE : wreath.image}
              alt={wreath.name}
              onError={() => setImgError(true)}
              className="w-full h-full object-cover"
            />
            {!wreath.inStock && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <span className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold text-xl">
                  품절
                </span>
              </div>
            )}
          </div>

          {/* 정보 영역 */}
          <div className="flex flex-col">
            <div className="mb-3 flex items-center gap-2">
              <span
                className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium ${getCategoryBadgeColor(
                  wreath.category
                )}`}
              >
                {wreath.category}
              </span>
              {alreadyInCart && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  <Check size={14} />
                  장바구니에 있음
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-primary mb-4">{wreath.name}</h1>

            <div className="mb-6 pb-6 border-b border-accent-200">
              <span className="text-3xl md:text-4xl font-bold text-primary">
                {formatCurrency(wreath.price)}
              </span>
            </div>

            <div className="mb-6">
              <h2 className="text-lg font-bold text-primary mb-2">상품 설명</h2>
              <p className="text-accent-700 leading-relaxed">{wreath.description}</p>
            </div>

            <div className="mb-6">
              <h2 className="text-lg font-bold text-primary mb-2">구성 내용</h2>
              <ul className="space-y-2">
                {wreath.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-accent-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* 근조 리본 문구 입력 */}
            <div className="mb-6 p-4 rounded-lg border border-accent-200 bg-white">
              <h2 className="text-lg font-bold text-primary mb-1">근조 리본 문구</h2>
              <p className="text-xs text-accent-600 mb-3">
                좌/우 리본 문구와 보내는 분을 입력하면 화환에 함께 제작됩니다. (선택)
              </p>

              {/* 추천 템플릿 */}
              <div className="mb-3">
                <p className="text-xs font-medium text-accent-700 mb-1.5">추천 문구 (왼쪽 리본에 적용)</p>
                <div className="flex flex-wrap gap-2">
                  {RIBBON_TEMPLATES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => applyTemplate('left', t)}
                      className="px-2.5 py-1 text-xs rounded-full border border-accent-300 text-accent-700 hover:border-primary hover:text-primary transition-colors"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Input
                  label="왼쪽 리본 문구"
                  name="ribbonLeft"
                  value={ribbon.left}
                  onChange={(e) => setRibbon((p) => ({ ...p, left: e.target.value }))}
                  placeholder="예: 謹弔 (근조)"
                />
                <Input
                  label="오른쪽 리본 문구"
                  name="ribbonRight"
                  value={ribbon.right}
                  onChange={(e) => setRibbon((p) => ({ ...p, right: e.target.value }))}
                  placeholder="예: 삼가 고인의 명복을 빕니다"
                />
                <Input
                  label="보내는 분"
                  name="ribbonSender"
                  value={ribbon.sender}
                  onChange={(e) => setRibbon((p) => ({ ...p, sender: e.target.value }))}
                  placeholder="예: 홍길동 / ○○회사 임직원 일동"
                />
              </div>

              {/* 미리보기 */}
              {(ribbon.left || ribbon.right || ribbon.sender) && (
                <div className="mt-4 p-4 rounded-lg bg-accent-50 border border-accent-200">
                  <p className="text-xs text-accent-600 mb-2">미리보기</p>
                  <div className="flex items-stretch justify-between gap-4">
                    <div className="flex-1 text-center py-3 bg-white rounded border border-accent-200">
                      <p className="text-sm font-bold text-primary whitespace-pre-line break-words">
                        {ribbon.left || '—'}
                      </p>
                      <p className="mt-1 text-xs text-accent-600 break-words">
                        {ribbon.sender ? `${ribbon.sender}` : ''}
                      </p>
                    </div>
                    <div className="flex-1 text-center py-3 bg-white rounded border border-accent-200">
                      <p className="text-sm font-bold text-primary whitespace-pre-line break-words">
                        {ribbon.right || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 수량 선택 */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-primary mb-3">수량</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDecrease}
                  disabled={!wreath.inStock || quantity <= 1}
                  className="w-10 h-10 flex items-center justify-center rounded-md border border-accent-300 hover:bg-accent-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="수량 감소"
                >
                  <Minus size={20} className="text-primary" />
                </button>

                <input
                  type="text"
                  inputMode="numeric"
                  value={quantity}
                  onChange={handleQuantityInput}
                  disabled={!wreath.inStock}
                  aria-label="수량"
                  className="w-16 h-10 text-center text-xl font-bold text-primary border border-accent-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-accent-100"
                />

                <button
                  onClick={handleIncrease}
                  disabled={!wreath.inStock || quantity >= MAX_QUANTITY}
                  className="w-10 h-10 flex items-center justify-center rounded-md border border-accent-300 hover:bg-accent-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="수량 증가"
                >
                  <Plus size={20} className="text-primary" />
                </button>
                <span className="text-xs text-accent-500">최대 {MAX_QUANTITY}개</span>
              </div>
            </div>

            {/* 총 금액 */}
            <div className="mb-6 p-4 bg-accent-50 rounded-lg border border-accent-200">
              <div className="flex items-center justify-between">
                <span className="text-lg font-medium text-accent-700">총 금액</span>
                <span className="text-2xl font-bold text-primary">
                  {formatCurrency(wreath.price * quantity)}
                </span>
              </div>
            </div>

            {/* 구매 버튼 */}
            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
              <Button
                variant="outline"
                size="lg"
                fullWidth
                onClick={handleAddToCart}
                disabled={!wreath.inStock}
                className="flex items-center justify-center gap-2"
              >
                <ShoppingCart size={20} />
                장바구니 담기
              </Button>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleBuyNow}
                disabled={!wreath.inStock}
              >
                바로 구매
              </Button>
            </div>

            {wreath.inStock && (
              <div className="mt-6 p-4 bg-accent-50 rounded-lg border border-accent-200">
                <h3 className="font-bold text-primary mb-2 text-sm">배송 안내</h3>
                <ul className="text-xs text-accent-700 space-y-1">
                  <li>• 주문 후 2-3시간 내 배송</li>
                  <li>• 장례식장 직접 배송</li>
                  <li>• 배송 전 확인 연락</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default WreathDetailPage;
